import { api, direRefus, parametres } from '../api.ts';
import { $, brut, euros, h, jour, mot, peindre, valeur, type Brut } from '../ui.ts';
import { type Referentiel, type Session } from '../etat.ts';
import { ETAPES_CHAINE, NIVEAUX_IA, POSTURES_IA, TERRAINS_IA } from '../../domaine/ia.ts';
import { TYPES_ENGAGEMENT as TYPES } from '../../domaine/vivier.ts';

export interface LigneProfil {
  id: string; prenom: string; nom: string; role_grille: string | null; role_libelle: string | null;
  famille: string | null; titre: string | null; types: string[]; statut: string;
  tjm_eur: number | null; salaire_min_eur: number | null; salaire_max_eur: number | null;
  marge_regie: number | null; dispo_texte: string | null; dispo_le: string | null;
  disponible_a_partir_de: string | null; cout_jour: number | null;
  tj_equivalent_min: number | null; tj_equivalent_max: number | null;
  fraicheur_jours: number | null; zones: string[]; ia_posture: string[] | null; contact_le: string | null;
  proprietaire: string | null; origine: string | null; a_prefiller: boolean;
  a_resynthetiser: boolean; ia_niveau: number | null;
}

const COLONNES = '300px 100px 150px 150px 116px 1fr 120px';

/**
 * Les filtres posés. Chaque facette porte une **liste** : on cherche « un consultant senior
 * ou un chef de projet », pas l'un puis l'autre. À l'intérieur d'une facette les valeurs
 * s'additionnent (OU) ; d'une facette à l'autre elles se cumulent (ET) — on coche pour
 * resserrer, et deux facettes disent deux exigences.
 */
export const FACETTES = ['famille', 'role', 'type', 'statut', 'ia', 'niveau', 'chaine', 'terrain'] as const;
export type Facette = (typeof FACETTES)[number];

const filtres: { q: string; page: number; fraiche: boolean; entrees: boolean;
                 prefiller: boolean; resynthese: boolean } & Record<Facette, string[]> = {
  q: '', famille: [], role: [], type: [], statut: [], ia: [], niveau: [], chaine: [], terrain: [],
  fraiche: false, entrees: false, prefiller: false, resynthese: false, page: 1,
};

/** Les interrupteurs, qui ne portent pas de valeur mais une intention. */
const DRAPEAUX = {
  fraiche: 'Donnée fraîche', entrees: 'Entrées du site à prendre',
  prefiller: 'CV à lire', resynthese: 'Synthèse à refaire',
} as const;

/**
 * Chaque facette se replie. Ouvertes sur un écran large, fermées sur un téléphone : huit
 * facettes dépliées y font défiler trois écrans avant d'atteindre la liste. Une facette qui
 * porte un filtre reste ouverte quoi qu'il arrive — la replier cacherait ce qui restreint
 * la liste, et c'est le meilleur moyen de ne plus comprendre ce qu'on regarde.
 */
const CLE_REPLI = 'mana.facettes.fermees';
const etroit = () => { try { return window.matchMedia('(max-width: 860px)').matches; } catch { return false; } };
const fermees = (): Set<string> => {
  try {
    const brut_ = localStorage.getItem(CLE_REPLI);
    if (brut_ !== null) return new Set(JSON.parse(brut_) as string[]);
  } catch { /* navigation privée */ }
  return etroit() ? new Set(FACETTES) : new Set();
};
const noterRepli = (cle: string, ouverte: boolean): void => {
  const s = fermees();
  if (ouverte) s.delete(cle); else s.add(cle);
  try { localStorage.setItem(CLE_REPLI, JSON.stringify([...s])); } catch { /* navigation privée */ }
};

/**
 * La colonne de filtres se replie, et s'en souvient. Le tableau est large : sur un
 * portable, récupérer 236 pixels change ce qu'on lit sans avoir à faire défiler.
 * Le choix est une préférence d'écran, pas une donnée : il vit dans le navigateur.
 */
const CLE_VOLET = 'mana.filtres.ouvert';
let volet = (() => { try { return localStorage.getItem(CLE_VOLET) !== 'non'; } catch { return true; } })();

export function basculerVolet(): void {
  volet = !volet;
  try { localStorage.setItem(CLE_VOLET, volet ? 'oui' : 'non'); } catch { /* navigation privée */ }
}

/** Combien de valeurs restreignent la liste — et non combien de facettes sont touchées. */
const actifs = () => FACETTES.reduce((n, c) => n + filtres[c].length, 0)
  + (Object.keys(DRAPEAUX) as (keyof typeof DRAPEAUX)[]).filter((d) => filtres[d]).length;

/** Vider d'un coup : une liste vide est un état, pas un filtre à retirer un par un. */
export function viderFiltres(): void {
  for (const c of FACETTES) filtres[c] = [];
  for (const d of Object.keys(DRAPEAUX) as (keyof typeof DRAPEAUX)[]) filtres[d] = false;
  filtres.q = '';
  filtres.page = 1;
}

/** Poser ou retirer une valeur dans une facette. Le même clic fait les deux. */
export function basculerFiltre(cle: Facette, valeur: string): void {
  const dans = filtres[cle];
  filtres[cle] = dans.includes(valeur) ? dans.filter((x) => x !== valeur) : [...dans, valeur];
  filtres.page = 1;
}

const pastille = (jours: number | null) =>
  jours == null ? 'var(--basalt)' : jours <= 30 ? 'var(--vert)' : jours <= 90 ? 'var(--ambre)' : 'var(--rouge)';

/**
 * Ce que coûte cette personne, dans l'unité où elle l'a dit. Un salaire est doublé de son
 * équivalent journalier : c'est la seule façon de lire une ligne de salarié et une ligne
 * d'indépendant sans faire la division de tête.
 */
export const condition = (p: LigneProfil) =>
  p.tjm_eur ? euros(p.tjm_eur)
    : p.salaire_min_eur
      ? `${(p.salaire_min_eur / 1000).toFixed(0)} – ${((p.salaire_max_eur ?? p.salaire_min_eur) / 1000).toFixed(0)} k€`
      : '—';

export const equivalentJour = (p: LigneProfil) =>
  p.tjm_eur || p.tj_equivalent_min == null ? null
    : p.tj_equivalent_max && p.tj_equivalent_max !== p.tj_equivalent_min
      ? `≈ ${p.tj_equivalent_min} – ${p.tj_equivalent_max} €/j`
      : `≈ ${p.tj_equivalent_min} €/j`;

const marge = (p: LigneProfil) =>
  p.marge_regie == null ? h`<span class="pt sourd">—</span>`
    : h`<span class="pt" style="color:${p.marge_regie >= 25 ? 'var(--vert)' : p.marge_regie >= 18 ? 'var(--ambre)' : 'var(--rouge)'}">
          marge ${p.marge_regie} % en régie</span>`;

const ligne = (p: LigneProfil) => h`
  <button class="tr" data-act="profil" data-id="${p.id}" style="grid-template-columns:${COLONNES}">
    <div><div class="rang" style="gap:7px">
        <span style="font-size:13.5px;color:var(--obsidian);font-weight:bold">${p.prenom} ${p.nom}</span>
        ${p.statut === 'qualifie' ? h`<span class="et v">Qualifié</span>` : h`<span class="et a">À qualifier</span>`}
        ${p.proprietaire === 'SITE' ? h`<span class="et c">Entrée du site</span>` : ''}
        ${p.a_prefiller ? h`<span class="et c">CV à lire</span>` : ''}
        ${p.a_resynthetiser ? h`<span class="et c">Synthèse à refaire</span>` : ''}
      </div>
      <div class="sm sourd" style="margin-top:3px">${p.role_libelle ?? p.titre ?? '—'}
        ${p.origine ? h`<span class="pt sourd">· via ${p.origine}</span>` : ''}</div></div>
    <span class="sm">${p.types.join(' · ') || '—'}</span>
    <div class="rang" style="gap:6px">
      <span class="point" style="background:${pastille(p.fraicheur_jours)}"></span>
      <span style="font-size:12px">${p.dispo_texte ?? '—'}</span>
      ${p.fraicheur_jours != null ? h`<span class="pt sourd">· ${p.fraicheur_jours} j</span>` : ''}</div>
    <div><div style="font-size:12px;color:var(--obsidian)">${condition(p)}
        ${equivalentJour(p) ? h`<span class="pt sourd">${equivalentJour(p)}</span>` : ''}</div>${marge(p)}</div>
    <div><span class="sm">${(p.ia_posture ?? []).join(' · ') || '—'}</span>
      ${p.ia_niveau ? h`<span class="pt" style="color:var(--copper)">${p.ia_niveau}/5 perçu</span>` : ''}</div>
    <span class="sm">${p.zones.join(', ') || '—'}</span>
    <span class="sm sourd">${jour(p.contact_le)}</span>
  </button>`;

/**
 * Une facette : un titre qui se replie, et des valeurs qui se cochent à plusieurs.
 *
 * `<details>` natif plutôt qu'un accordéon écrit à la main : il s'ouvre au clavier, se
 * lit correctement par un lecteur d'écran, et tient sans une ligne de script. L'état se
 * note au `toggle`, et une facette qui porte un filtre s'ouvre de force.
 */
const facette = (titre: string, cle: Facette, choix: { valeur: string; libelle: string }[]) => {
  const poses = filtres[cle];
  const ouverte = poses.length > 0 || !fermees().has(cle);
  return h`
    <details class="facette" data-facette="${cle}" ${ouverte ? brut('open') : ''}>
      <summary><span class="sur">${titre}</span>
        ${poses.length ? h`<span class="et">${poses.length}</span>` : ''}
        <span class="chevron" aria-hidden="true"></span></summary>
      <div class="choix">
        ${choix.map((c) => h`<button class="opt" data-act="filtre" data-cle="${cle}" data-valeur="${c.valeur}"
            aria-pressed="${poses.includes(c.valeur)}">
            <span class="case" aria-hidden="true"></span>${c.libelle}</button>`)}
      </div></details>`;
};

/**
 * Le libellé d'une valeur de facette, pour l'étiquette qui la rappelle au-dessus de la
 * liste. Les clés techniques — `fde_senior`, `poc` — n'ont rien à faire sous les yeux de
 * quelqu'un : une étiquette qu'on ne sait pas lire ne se retire pas, elle s'endure.
 */
const libelleValeur = (cle: Facette, valeur: string, ref: Referentiel): string => {
  switch (cle) {
    case 'famille': return ref.familles[valeur as keyof typeof ref.familles] ?? valeur;
    case 'role': return ref.roles.find((r) => r.cle === valeur)?.libelle ?? valeur;
    case 'chaine': return ETAPES_CHAINE.find((e) => e.cle === valeur)?.libelle ?? valeur;
    case 'terrain': return TERRAINS_IA.find((x) => x.cle === valeur)?.libelle ?? valeur;
    case 'niveau': return `Profondeur ${valeur} · ${NIVEAUX_IA[Number(valeur)] ?? ''}`;
    case 'statut': return valeur === 'qualifie' ? 'Qualifié' : 'À qualifier';
    default: return valeur;
  }
};

/**
 * Ce qui restreint la liste, en toutes lettres et retirable d'un clic.
 *
 * La colonne de filtres dit ce qu'on peut cocher ; elle ne dit pas, quand elle est repliée
 * ou qu'on l'a quittée des yeux, pourquoi la liste est courte. Les étiquettes le disent au
 * seul endroit où la question se pose : juste au-dessus des résultats.
 */
const etiquettes = (ref: Referentiel): Brut => {
  const poses: Brut[] = [];
  if (filtres.q)
    poses.push(h`<button class="tag" data-act="retirer-q" title="Retirer ce mot de la recherche">
      <span class="pt sourd">texte</span> ${filtres.q}<span class="x" aria-hidden="true">✕</span></button>`);
  for (const cle of FACETTES)
    for (const v of filtres[cle])
      poses.push(h`<button class="tag" data-act="filtre" data-cle="${cle}" data-valeur="${v}"
        title="Retirer ce filtre">${libelleValeur(cle, v, ref)}<span class="x" aria-hidden="true">✕</span></button>`);
  for (const [cle, libelle] of Object.entries(DRAPEAUX))
    if (filtres[cle as keyof typeof DRAPEAUX])
      poses.push(h`<button class="tag" data-act="${cle}" title="Retirer ce filtre">${libelle}<span class="x" aria-hidden="true">✕</span></button>`);

  if (!poses.length) return h``;
  return h`<div class="tags">
    <span class="pt sourd">Filtres actifs</span>
    ${poses}
    <button class="sm" data-act="raz" style="color:var(--copper)">Tout effacer</button></div>`;
};

/** Un interrupteur : pas une valeur à choisir, une intention à poser. */
const drapeau = (cle: keyof typeof DRAPEAUX, libelle: string, aide: string) => h`
  <button class="opt" data-act="${cle}" aria-pressed="${filtres[cle]}">
    <span class="case" aria-hidden="true"></span>${libelle}
    <span class="pt sourd">${aide}</span></button>`;

export async function ecranVivier(session: Session, ref: Referentiel, chrome: (corps: Brut) => Brut): Promise<void> {
  const charger = async () => {
    const r = await api.get<{ total: number; profils: LigneProfil[] }>('/api/profils' + parametres(filtres));
    const poses = actifs();

    peindre('#app', chrome(h`
      <div class="duo">
        <aside class="cote" ${volet ? '' : brut('hidden')}>
          <div class="entre" style="align-items:center;margin-bottom:4px">
            <span class="sur">FILTRES${poses ? ` · ${poses}` : ''}</span>
            ${poses ? h`<button class="sm" data-act="raz" style="color:var(--copper)">Tout effacer</button>` : ''}
          </div>
          ${facette('FAMILLE DE PROFIL', 'famille',
            Object.entries(ref.familles).map(([valeur, libelle]) => ({ valeur, libelle })))}
          ${facette('RÔLE · GRILLE WAKEA', 'role', ref.roles.map((x) => ({ valeur: x.cle, libelle: x.libelle })))}
          ${facette('CHAÎNE IA · SOLIDE OU MIEUX', 'chaine',
            ETAPES_CHAINE.map((e) => ({ valeur: e.cle, libelle: e.libelle })))}
          ${facette('TERRAIN IA · SOLIDE OU MIEUX', 'terrain',
            TERRAINS_IA.map((t) => ({ valeur: t.cle, libelle: t.libelle })))}
          ${facette('POSTURE IA', 'ia', POSTURES_IA.map((x) => ({ valeur: x, libelle: x })))}
          ${facette('PROFONDEUR', 'niveau',
            [1, 2, 3, 4, 5].map((n) => ({ valeur: String(n), libelle: `${n} · ${NIVEAUX_IA[n]}` })))}
          ${facette('TYPE', 'type', TYPES.map((x) => ({ valeur: x, libelle: x })))}
          ${facette('ÉTAT', 'statut',
            [['qualifie', 'Qualifié'], ['a_qualifier', 'À qualifier']].map(([v, l]) => ({ valeur: v!, libelle: l! })))}
          <div class="facette ouverte"><span class="sur">À TRAITER</span>
            <div class="choix">
              ${drapeau('fraiche', 'Donnée fraîche', 'moins de 90 jours')}
              ${drapeau('entrees', 'Entrées du site', 'personne ne les porte encore')}
              ${drapeau('prefiller', 'CV à lire', 'pré-remplissage à faire')}
              ${drapeau('resynthese', 'Synthèse à refaire', 'consigne antérieure')}
            </div></div>
        </aside>

        <div class="page pousse" style="min-width:0">
          <div class="entre"><div><h1 class="h1">Vivier</h1>
            <p class="sous" style="margin:4px 0 0">${r.total} profil${r.total > 1 ? 's' : ''}${
              poses || filtres.q ? ' après filtrage' : ''}</p></div>
            ${session.role === 'lecture' ? '' : h`<div>
              <div class="sur" style="text-align:right">AJOUTER UN PROFIL AU VIVIER</div>
              <div class="rang" style="gap:8px;margin-top:7px">
                <button class="bt" data-act="nouveau">Saisir à la main</button>
                <button class="bt p" data-act="lire-cv">À partir d'un CV</button></div></div>`}</div>

          <form id="rech" class="barre-rech">
            <button class="bt volet" type="button" data-act="volet" aria-pressed="${volet}"
              title="${volet ? 'Masquer la colonne de filtres' : 'Afficher la colonne de filtres'}">
              <span class="chevron ${volet ? 'gauche' : 'droite'}" aria-hidden="true"></span>
              Filtres${poses ? h`<span class="et">${poses}</span>` : ''}</button>
            <input class="champ" id="q" value="${filtres.q}" autocomplete="off"
              placeholder="Par exemple : data scientist retail disponible Lille">
            <button class="bt p" type="submit">Rechercher</button></form>

          ${etiquettes(ref)}


          <div class="large"><div>
            <div class="th" style="grid-template-columns:${COLONNES}">
              ${['PROFIL · RÔLE GRILLE', 'TYPE', 'DISPONIBILITÉ', 'CONDITION', 'POSTURE IA', 'ZONE', 'DERNIER CONTACT']
                .map((t) => h`<span>${t}</span>`)}</div>
            ${r.profils.length ? r.profils.map(ligne)
              : h`<p class="sm sourd" style="padding:30px 8px">Aucun profil ne répond à ces filtres.</p>`}
          </div></div>
        </div></div>`));

    $('#rech')?.addEventListener('submit', (ev) => {
      ev.preventDefault(); filtres.q = valeur('#q'); filtres.page = 1; void charger();
    });

    // Le repli d'une facette se note au moment où il arrive. `<details>` gère l'ouverture
    // lui-même ; on ne fait que s'en souvenir d'une visite à l'autre.
    for (const d of document.querySelectorAll<HTMLDetailsElement>('details.facette'))
      d.addEventListener('toggle', () => noterRepli(d.dataset['facette']!, d.open));
  };

  await charger().catch((e) => mot(direRefus(e)));
}

export const etatFiltres = filtres;
